import { get as getConfig } from 'config'
import { all as allPromise, Promise as BluebirdPromise } from 'bluebird'
import { List, fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import {
  isYoga as isUrlYoga,
  isFitness as isUrlFitness,
  isFilm as isUrlFilm,
  isRecipe as isUrlRecipe,
  isOriginal as isUrlOriginal,
  isConscious as isUrlConscious,
  isMeditation as isUrlMeditation,
} from 'services/url'
import {
  get as getFilter,
  TYPE_SORT_BY,
  TYPE_YOGA_SPECIALITY,
  TYPE_YOGA_LEVEL,
  TYPE_YOGA_STYLE,
  TYPE_YOGA_TEACHER,
  TYPE_YOGA_DURATION,
  TYPE_FITNESS_SPECIALITY,
  TYPE_FITNESS_LEVEL,
  TYPE_FITNESS_STYLE,
  TYPE_FITNESS_TEACHER,
  TYPE_FITNESS_DURATION,
  TYPE_FILM_TYPE,
  TYPE_RECIPE_TYPE,
  TYPE_RECIPE_DIET,
  TYPE_INTRV_HOST,
  TYPE_INTRV_FEATURED_GUEST,
  TYPE_CONSCIOUS_FORMAT,
  TYPE_CONSCIOUS_TOPIC,
  TYPE_MEDITATION_FOCUS,
  TYPE_MEDITATION_STYLE,
  TYPE_MEDITATION_TEACHER,
  TYPE_MEDITATION_DURATION,
} from 'services/filter'
import {
  DEFAULT_PATH_YOGA,
  DEFAULT_PATH_FITNESS,
  DEFAULT_PATH_FILM,
  DEFAULT_PATH_RECIPE,
  DEFAULT_PATH_ORIGINAL,
  DEFAULT_PATH_CONSCIOUS,
  DEFAULT_PATH_MEDITATION,
} from 'services/url/constants'

const config = getConfig()

export const FILTER_SET_DEFAULT = 'default'
export const FILTER_SET_YOGA = 'yoga'
export const FILTER_SET_FITNESS = 'fitness'
export const FILTER_SET_FILM = 'film'
export const FILTER_SET_RECIPE = 'recipe'
export const FILTER_SET_ORIGINAL = 'interview'
export const FILTER_SET_CONSCIOUS = 'conscious'
export const FILTER_SET_MEDITATION = 'meditation'

const VALID_FILTER_SETS = List([
  FILTER_SET_DEFAULT,
  FILTER_SET_YOGA,
  FILTER_SET_FITNESS,
  FILTER_SET_FILM,
  FILTER_SET_RECIPE,
  FILTER_SET_ORIGINAL,
  FILTER_SET_CONSCIOUS,
  FILTER_SET_MEDITATION,
])

function omitInvalidOptions (filter) {
  if (filter) {
    return filter.update('options', (options) => options.filter((option) => (!!option.get('name', null))))
  }
  return filter
}

/** Get the filter set */
export async function get (options = {}) {
  const { filterSet, language } = options
  if (!filterSet) {
    return BluebirdPromise.reject(new Error('The filterSet option is required.'))
  }
  const filterSetData = getFilterSetData(filterSet)
  const promises = filterSetData.map((val) => getFilter({
    type: val.get('type'),
    allOptionPath: val.get('allOptionPath'),
    language,
  })).toJS()

  const states = await allPromise(promises)
  const data = filterSetData.map((val, index) => {
    const filter = omitInvalidOptions(_get(states, index))
    return val.set('filter', filter)
  })
  return Map({ _dataError: undefined, data })
}

export function getSetByPathname (pathname) {
  if (isUrlFitness(pathname)) {
    return FILTER_SET_FITNESS
  } if (isUrlYoga(pathname)) {
    return FILTER_SET_YOGA
  } if (isUrlFilm(pathname)) {
    return FILTER_SET_FILM
  } if (isUrlRecipe(pathname)) {
    return FILTER_SET_RECIPE
  } if (isUrlOriginal(pathname)) {
    return FILTER_SET_ORIGINAL
  } if (isUrlConscious(pathname)) {
    return FILTER_SET_CONSCIOUS
  } if (isUrlMeditation(pathname)) {
    return FILTER_SET_MEDITATION
  }
  return FILTER_SET_DEFAULT
}

export function isValid (filterSet) {
  return VALID_FILTER_SETS.includes(filterSet)
}

export function getFilterSetData (filterSet) {
  let filterSetData = []
  switch (filterSet) {
    case FILTER_SET_YOGA:
      filterSetData = [
        {
          type: TYPE_YOGA_STYLE,
          allOptionPath: DEFAULT_PATH_YOGA,
          param: 'style',
        },
        {
          type: TYPE_YOGA_TEACHER,
          allOptionPath: DEFAULT_PATH_YOGA,
          param: 'teacher',
        },
        {
          type: TYPE_YOGA_LEVEL,
          allOptionPath: DEFAULT_PATH_YOGA,
          param: 'level',
        },
        {
          type: TYPE_YOGA_DURATION,
          allOptionPath: DEFAULT_PATH_YOGA,
          param: 'duration',
        },
        {
          type: TYPE_YOGA_SPECIALITY,
          allOptionPath: DEFAULT_PATH_YOGA,
          param: 'specialty',
        },
      ]
      break
    case FILTER_SET_FITNESS:
      filterSetData = [
        {
          type: TYPE_FITNESS_STYLE,
          allOptionPath: DEFAULT_PATH_FITNESS,
          param: 'style',
        },
        {
          type: TYPE_FITNESS_TEACHER,
          allOptionPath: DEFAULT_PATH_FITNESS,
          param: 'instructor',
        },
        {
          type: TYPE_FITNESS_LEVEL,
          allOptionPath: DEFAULT_PATH_FITNESS,
          param: 'level',
        },
        {
          type: TYPE_FITNESS_DURATION,
          allOptionPath: DEFAULT_PATH_FITNESS,
          param: 'duration',
        },
        {
          type: TYPE_FITNESS_SPECIALITY,
          allOptionPath: DEFAULT_PATH_FITNESS,
          param: 'specialty',
        },
      ]
      break
    case FILTER_SET_FILM:
      filterSetData = [
        {
          type: TYPE_FILM_TYPE,
          allOptionPath: DEFAULT_PATH_FILM,
          param: 'type',
        },
        /*
        {
          type: TYPE_FILM_SUBJECT,
          allOptionPath: DEFAULT_PATH_FILM,
          param: 'subject',
        },
        */
      ]
      break
    case FILTER_SET_RECIPE:
      filterSetData = [
        {
          type: TYPE_RECIPE_TYPE,
          allOptionPath: DEFAULT_PATH_RECIPE,
          param: 'type',
        },
        {
          type: TYPE_RECIPE_DIET,
          allOptionPath: DEFAULT_PATH_RECIPE,
          param: 'diet',
        },
      ]
      break
    case FILTER_SET_ORIGINAL:
      filterSetData = [
        {
          type: TYPE_INTRV_HOST,
          allOptionPath: DEFAULT_PATH_ORIGINAL,
          param: 'host',
        },
        {
          type: TYPE_INTRV_FEATURED_GUEST,
          allOptionPath: DEFAULT_PATH_ORIGINAL,
          param: 'featured_guest',
        },
        /*
        { type: TYPE_INTRV_COLLECTION, allOptionPath: DEFAULT_PATH_ORIGINAL, param: 'collection' },
        { type: TYPE_INTRV_TOPIC, allOptionPath: DEFAULT_PATH_ORIGINAL, param: 'topic' }
        */
      ]
      break
    case FILTER_SET_CONSCIOUS:
      if (_get(config, ['features', 'filters', FILTER_SET_CONSCIOUS], false)) {
        filterSetData = [
          {
            type: TYPE_CONSCIOUS_FORMAT,
            allOptionPath: DEFAULT_PATH_CONSCIOUS,
            param: 'format',
          },
          {
            type: TYPE_CONSCIOUS_TOPIC,
            allOptionPath: DEFAULT_PATH_CONSCIOUS,
            param: 'topic',
          },
          /*
          {type: TYPE_CONSCIOUS_DURATION, allOptionPath: DEFAULT_PATH_CONSCIOUS, param: 'duration'},
          */
        ]
      }
      break
    case FILTER_SET_MEDITATION:
      filterSetData = [
        {
          type: TYPE_MEDITATION_FOCUS,
          allOptionPath: DEFAULT_PATH_MEDITATION,
          param: 'specialty',
        },
        {
          type: TYPE_MEDITATION_STYLE,
          allOptionPath: DEFAULT_PATH_MEDITATION,
          param: 'style',
        },
        {
          type: TYPE_MEDITATION_TEACHER,
          allOptionPath: DEFAULT_PATH_MEDITATION,
          param: 'teacher',
        },
        {
          type: TYPE_MEDITATION_DURATION,
          allOptionPath: DEFAULT_PATH_MEDITATION,
          param: 'duration',
        },
      ]
      break
    default:
      filterSetData = [
        { type: TYPE_SORT_BY, allOptionPath: null, param: 'sort' },
      ]
  }

  return fromJS(filterSetData)
}
/**
 * Custom comparator function for sorting filters options
 * @param {Map} a - option A to compare
 * @param {Map} b - option B to compare
 * @param {String} userLanguage - user language
 * @returns {Number} - -1 or 0 or 1
 */
export const filterOptionsCompare = (a, b, userLanguage) => {
  const nameA = a.get('name')
  const nameB = b.get('name')
  // Value seems to always be null for the 'All' option
  const value = b.get('value')

  // To account for the 'All' option always being first
  if (value === null) {
    return 1
  }

  return nameA.localeCompare(nameB, userLanguage, { numeric: true, sensitivity: 'base' })
}
